import React, { useEffect, useState, useCallback } from 'react';
import API from '../api';
import { toast } from 'react-toastify';

export default function ReturnsHistoryPage() {
  // 1. Check for cached data for instant load
  const cachedReturns = sessionStorage.getItem('returnsList');
  const [returns, setReturns] = useState(cachedReturns ? JSON.parse(cachedReturns) : []);
  const [loading, setLoading] = useState(!cachedReturns); // Only show spinner if no cache
  
  const [filter, setFilter] = useState('Pending');
  
  const [approveModal, setApproveModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);

  const [actualAmount, setActualAmount] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  
  const [rejectReason, setRejectReason] = useState('');
  const [returnToStock, setReturnToStock] = useState(false);

  // ---------- RESPONSIVE STATE ----------
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchReturns = useCallback(async () => {
    // Only set loading to true if we have absolutely no data (allows silent background updates)
    if (returns.length === 0 && !sessionStorage.getItem('returnsList')) {
      setLoading(true);
    }
    
    try {
      const { data } = await API.get('/purchase-returns');
      setReturns(data);
      // 2. Cache the freshly fetched data
      sessionStorage.setItem('returnsList', JSON.stringify(data));
    } catch (err) { 
      toast.error('Failed to load returns history'); 
    } finally { 
      setLoading(false); 
    }
  }, [returns.length]);

  useEffect(() => { 
    fetchReturns(); 
  }, [fetchReturns]);

  const handleApprove = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/purchase-returns/${approveModal._id}/approve`, {
        actualApprovedAmount: actualAmount,
        vendorReceiptNumber: receiptNo
      });
      toast.success('Return Approved & Ledger Updated!');
      setApproveModal(null);
      fetchReturns();
    } catch (err) { 
      toast.error('Failed to approve'); 
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/purchase-returns/${rejectModal._id}/reject`, {
        rejectionReason: rejectReason, 
        returnToStock
      });
      toast.success('Return Rejected');
      setRejectModal(null);
      fetchReturns();
    } catch (err) { 
      toast.error('Failed to reject'); 
    }
  };

  const handleViewReport = async (returnId) => {
    try {
      const { data } = await API.get(`/purchase-returns/${returnId}/report`);
      const blob = new Blob([data.reportHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      
      if (!printWindow) {
        toast.error('Please allow pop-ups for this site to view reports');
        URL.revokeObjectURL(url);
        return;
      }
      printWindow.addEventListener('load', () => { 
        setTimeout(() => { 
          try { printWindow.print(); } catch (err) {} 
        }, 500); 
      });
    } catch (err) { 
      toast.error('No report found for this return'); 
    }
  };

  const filteredReturns = returns.filter(r => filter === 'ALL' || r.status === filter);
  const fmt = (num) => Number(num || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ---------- STYLES ----------
  const styles = {
    container: { minHeight: '100vh', background: '#f4f9fd', fontFamily: "'Inter', -apple-system, sans-serif" },
    header: { background: '#ffffff', borderBottom: '1px solid #e0f2fe', padding: isMobile ? '12px 16px' : '16px 24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 12 : 0, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)' },
    headerTitle: { display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, width: isMobile ? '100%' : 'auto' },
    headerIcon: { width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, background: 'linear-gradient(135deg, #1880c2, #0369a1)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: isMobile ? 11 : 13, fontWeight: 700, letterSpacing: '0.5px', flexShrink: 0 },
    headerText: { fontSize: isMobile ? 16 : 20, fontWeight: 700, color: '#1a202c', letterSpacing: '-0.3px' },
    headerSubtext: { fontSize: isMobile ? 10 : 12, color: '#718096', fontWeight: 500, marginTop: 2, display: isSmallMobile ? 'none' : 'block' },
    headerActions: { display: 'flex', alignItems: 'center', gap: 6, width: isMobile ? '100%' : 'auto', overflowX: 'auto', paddingBottom: isMobile ? 4 : 0 },
    tabBtn: (active) => ({
      padding: isMobile ? '6px 12px' : '8px 16px',
      background: active ? '#1880c2' : '#ffffff',
      color: active ? 'white' : '#4a5568',
      border: active ? '1px solid #1880c2' : '1px solid #e0f2fe',
      borderRadius: 6, 
      fontSize: isMobile ? 12 : 13, 
      fontWeight: 600, 
      cursor: 'pointer',
      transition: 'all 0.2s', 
      whiteSpace: 'nowrap',
      flex: isMobile ? '1 1 auto' : '0 0 auto',
      textAlign: 'center'
    }),
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '12px' : '24px 32px' },
    card: { background: '#ffffff', borderRadius: isMobile ? 8 : 10, border: '1px solid #e0f2fe', marginBottom: isMobile ? 16 : 24, boxShadow: '0 1px 2px rgba(24, 128, 194, 0.05)', overflow: 'hidden' },
    cardHeader: { padding: isMobile ? '12px 16px' : '16px 24px', borderBottom: '1px solid #e0f2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' },
    cardTitle: { fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#1a202c', margin: 0 },
    tableContainer: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 12 : 13, minWidth: '800px' },
    th: { padding: isMobile ? '10px 12px' : '12px 16px', textAlign: 'left', fontSize: isMobile ? 10 : 11, fontWeight: 600, color: '#718096', background: '#f4f9fd', borderBottom: '1px solid #e0f2fe', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
    td: { padding: isMobile ? '10px 12px' : '14px 16px', color: '#1a202c', borderBottom: '1px solid #f7fafc', verticalAlign: 'top' },
    badge: (status) => ({
      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, display: 'inline-block',
      background: status === 'Pending' ? '#fef3c7' : status === 'Approved' ? '#dcfce7' : '#fee2e2',
      color: status === 'Pending' ? '#b45309' : status === 'Approved' ? '#166534' : '#991b1b'
    }),
    actionBtns: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    btnSuccess: { padding: '6px 12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 11 : 12, transition: 'all 0.2s', touchAction: 'manipulation' },
    btnDanger: { padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 11 : 12, transition: 'all 0.2s', touchAction: 'manipulation' },
    btnPrimary: { padding: '6px 12px', background: '#1880c2', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 11 : 12, transition: 'all 0.2s', touchAction: 'manipulation' },
    btnSecondary: { padding: '8px 16px', background: '#ffffff', color: '#4a5568', border: '1px solid #e0f2fe', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 12 : 13, transition: 'all 0.2s', touchAction: 'manipulation' },
    btnConfirm: { padding: '8px 16px', background: '#1880c2', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 13 : 14, transition: 'all 0.2s', touchAction: 'manipulation' },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: isMobile ? 16 : 20 },
    modalContent: { background: '#ffffff', borderRadius: isMobile ? 12 : 16, width: isMobile ? '100%' : '450px', maxWidth: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    modalHeader: { padding: isMobile ? '16px' : '20px 24px', borderBottom: '1px solid #e0f2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: '#f8fafc' },
    modalBody: { padding: isMobile ? 16 : 24 },
    modalFooter: { padding: isMobile ? '16px' : '16px 24px', borderTop: '1px solid #e0f2fe', display: 'flex', justifyContent: 'flex-end', gap: 12, background: '#fafbfc' },
    label: { display: 'block', fontSize: isMobile ? 11 : 12, fontWeight: 600, color: '#4a5568', marginBottom: 6 },
    input: { padding: isMobile ? '12px 14px' : '10px 14px', borderRadius: 6, border: '1px solid #e1e8ed', fontSize: isMobile ? 16 : 14, width: '100%', boxSizing: 'border-box', background: '#fff', outline: 'none', transition: 'border-color 0.2s', touchAction: 'manipulation', WebkitAppearance: 'none' },
    checkboxLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: isMobile ? 13 : 13, color: '#1a202c', cursor: 'pointer', fontWeight: 500, marginTop: 16 }
  };

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <div style={styles.headerIcon}>RET</div>
          <div>
            <div style={styles.headerText}>Vendor Returns Workflow</div>
            <div style={styles.headerSubtext}>Manage and track purchase returns</div>
          </div>
        </div>
        <div style={styles.headerActions}>
          {['Pending', 'Approved', 'Rejected', 'ALL'].map(tab => (
            <button 
              key={tab} 
              onClick={() => setFilter(tab)} 
              style={styles.tabBtn(filter === tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main style={styles.main}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitle}>Returns History</div>
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date & ID</th>
                  <th style={styles.th}>Medicine</th>
                  <th style={styles.th}>Vendor (Profile)</th>
                  <th style={styles.th}>Expected Refund (Incl. VAT)</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && filteredReturns.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                      <div style={{ width: 32, height: 32, border: '3px solid #e0f2fe', borderTopColor: '#1880c2', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                      Loading returns...
                      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </td>
                  </tr>
                ) : filteredReturns.map(r => (
                  <tr key={r._id} style={{ background: r.status === 'Pending' ? '#fdf8f6' : 'transparent', transition: 'background 0.2s' }}>
                    <td style={styles.td}>
                      <strong style={{ color: '#1a202c' }}>{new Date(r.dateInitiated).toLocaleDateString()}</strong><br/>
                      <span style={{ fontSize: 11, color: '#64748b' }}>{r.returnInvoiceNumber}</span>
                    </td>
                    <td style={styles.td}>
                      <strong style={{ color: '#1a202c' }}>{r.medicine?.name}</strong><br/>
                      <span style={{ fontSize: 11, color: '#64748b' }}>Qty: {r.returnQty} units</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: '#1a202c', fontWeight: 500 }}>{r.party?.name}</span><br/>
                      <span style={{ fontSize: 10, padding: '2px 6px', background: r.supplierRegistrationType === 'VAT Registered' ? '#dcfce7' : '#f1f5f9', color: r.supplierRegistrationType === 'VAT Registered' ? '#166534' : '#475569', borderRadius: 4, display: 'inline-block', marginTop: 4, fontWeight: 600 }}>
                        {r.supplierRegistrationType || 'Unregistered'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Base: Rs {fmt(r.subtotal)}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>VAT: Rs {fmt(r.tax)}</div>
                      <div style={{ fontWeight: 700, marginTop: 4, color: '#1a202c' }}>Total: Rs {fmt(r.totalRequestedAmount)}</div>
                      {r.status === 'Approved' && (
                        <span style={{ color: '#16a34a', fontSize: 11, display: 'block', marginTop: 4, fontWeight: 600 }}>
                          Approved: Rs {fmt(r.actualApprovedAmount)}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(r.status)}>{r.status}</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={{ ...styles.actionBtns, justifyContent: 'center' }}>
                        {r.status === 'Pending' ? (
                          <>
                            <button onClick={() => { setApproveModal(r); setActualAmount(r.totalRequestedAmount); }} style={styles.btnSuccess}>Approve</button>
                            <button onClick={() => setRejectModal(r)} style={styles.btnDanger}>Reject</button>
                          </>
                        ) : (
                          <button onClick={() => handleViewReport(r._id)} style={styles.btnPrimary}>📄 Report</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredReturns.length === 0 && !loading && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                      <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>📦</div>
                      No returns found matching this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* APPROVE MODAL */}
      {approveModal && (
        <div style={styles.modal} onClick={() => setApproveModal(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: 18, color: '#1a202c' }}>Approve Credit Note</h2>
                <p style={{ margin: 0, fontSize: 12, color: '#718096' }}>Vendor accepted the return. Update ledger.</p>
              </div>
              <button onClick={() => setApproveModal(null)} style={{ background: 'none', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            
            <form onSubmit={handleApprove}>
              <div style={styles.modalBody}>
                <div style={{ marginBottom: 16 }}>
                  <label style={styles.label}>Approved Refund Amount (Rs)</label>
                  <input 
                    type="number" 
                    required 
                    value={actualAmount} 
                    onChange={e => setActualAmount(e.target.value)} 
                    style={styles.input} 
                    onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                    onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    autoFocus 
                  />
                </div>
                <div>
                  <label style={styles.label}>Vendor Receipt / Credit Note #</label>
                  <input 
                    type="text" 
                    value={receiptNo} 
                    onChange={e => setReceiptNo(e.target.value)} 
                    placeholder="e.g. CR-10293" 
                    style={styles.input} 
                    onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                    onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                  />
                </div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setApproveModal(null)} style={styles.btnSecondary}>Cancel</button>
                <button type="submit" style={{ ...styles.btnConfirm, background: '#16a34a' }}>Confirm & Credit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectModal && (
        <div style={styles.modal} onClick={() => setRejectModal(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: 18, color: '#1a202c' }}>Reject Return</h2>
                <p style={{ margin: 0, fontSize: 12, color: '#718096' }}>Deny return request and optional restock.</p>
              </div>
              <button onClick={() => setRejectModal(null)} style={{ background: 'none', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            
            <form onSubmit={handleReject}>
              <div style={styles.modalBody}>
                <div>
                  <label style={styles.label}>Rejection Reason</label>
                  <input 
                    type="text" 
                    required 
                    value={rejectReason} 
                    onChange={e => setRejectReason(e.target.value)} 
                    placeholder="e.g. Too damaged, policy expired" 
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                    onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} 
                    autoFocus 
                  />
                </div>
                <label style={styles.checkboxLabel}>
                  <input 
                    type="checkbox" 
                    checked={returnToStock} 
                    onChange={e => setReturnToStock(e.target.checked)} 
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  Add medicines back to physical stock
                </label>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setRejectModal(null)} style={styles.btnSecondary}>Cancel</button>
                <button type="submit" style={{ ...styles.btnConfirm, background: '#ef4444' }}>Confirm Rejection</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}